import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Popup from "components/popup/Popup";

type TEmailCampaignUnpublishConfirmModalProps = {
  open: boolean;
  confirming: boolean;
  scheduledType: string | null | undefined;
  onClose: () => void;
  onConfirm: () => void;
};

export function EmailCampaignUnpublishConfirmModal(props: TEmailCampaignUnpublishConfirmModalProps) {
  const { t } = useTranslation();
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const isRecurring = props.scheduledType === "recurring";

  useEffect(() => {
    if (!props.open) {
      return;
    }

    const active = window.document.activeElement;
    returnFocusRef.current = active instanceof HTMLElement ? active : null;
  }, [props.open]);

  useEffect(() => {
    if (props.open) {
      return;
    }

    const returnFocus = returnFocusRef.current;
    if (returnFocus !== null && typeof returnFocus.focus === "function") {
      returnFocus.focus();
    }
    returnFocusRef.current = null;
  }, [props.open]);

  useEffect(() => {
    if (!props.open || props.confirming) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        props.onClose();
      }
    }

    window.document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.document.removeEventListener("keydown", handleKeyDown);
    };
  }, [props.open, props.onClose, props.confirming]);

  function handleClose() {
    if (props.confirming) {
      return;
    }

    props.onClose();
  }

  return (
    <Popup
      open={props.open}
      type="warning"
      title={t("Unpublish campaign?")}
      description={
        isRecurring
          ? t(
              "Unpublishing stops all future sends for this recurring campaign and returns it to draft so you can edit it. You will need to schedule it again when you are ready.",
            )
          : t(
              "Unpublishing returns this campaign to draft so you can edit it. Your schedule is kept, but you will need to publish or schedule it again when you are ready.",
            )
      }
      closable
      onCancel={handleClose}
      cancelText={t("Cancel")}
      okText={props.confirming ? t("Unpublishing…") : t("Unpublish")}
      onOk={props.onConfirm}
      okDisabled={props.confirming}
      cancelDisabled={props.confirming}
      backDropCancel={!props.confirming}
      compactActions
    />
  );
}
